import React from "react";
import { InView } from "react-intersection-observer";

const testimonials = [
  {
    name: "Rajesh Kumar",
    position: "Owner, Royal Travels",
    feedback: "Nupura Self Drive Cars helped us streamline our vehicle bookings. The flexibility in rental durations is a game-changer for us!",
    rating: 5,
    avatar: "RK"
  },
  {
    name: "Neha Verma",
    position: "Manager, Dream Holidays",
    feedback: "We've been using Nupura Self Drive Cars for over a year now, and the customer support is outstanding. They make our booking process so smooth.",
    rating: 5,
    avatar: "NV"
  },
  {
    name: "Amit Gupta",
    position: "CEO, Elite Rides",
    feedback: "The variety of vehicles offered by Nupura Self Drive Cars is impressive. It's been a breeze for us to provide our customers with premium vehicles.",
    rating: 4,
    avatar: "AG"
  },
  {
    name: "Suman Joshi",
    position: "Operations Head, FastDrive Rentals",
    feedback: "The platform is intuitive, and we love the transparent pricing. Nupura Self Drive Cars has definitely raised the bar in the car rental industry.",
    rating: 5,
    avatar: "SJ"
  },
  {
    name: "Priya Sharma",
    position: "Director, City Commute",
    feedback: "Their service reliability and vehicle maintenance standards are exceptional. Our business has grown 40% since partnering with them.",
    rating: 5,
    avatar: "PS"
  },
  {
    name: "Rohit Mehta",
    position: "Founder, TravelEase Solutions",
    feedback: "The seamless booking experience and timely vehicle delivery make Nupura Self Drive Cars our preferred rental partner across all locations.",
    rating: 4,
    avatar: "RM"
  },
];

const ClientTestimonials = () => {
  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <span
        key={index}
        className={`text-lg ${
          index < rating ? "text-yellow-400" : "text-gray-300"
        }`}
      >
        ★
      </span>
    ));
  };

  const getGradient = (index) => {
    const gradients = [
      "from-purple-500 to-pink-500",
      "from-blue-500 to-cyan-500",
      "from-green-500 to-emerald-500",
      "from-orange-500 to-red-500",
      "from-indigo-500 to-purple-500",
      "from-teal-500 to-blue-500",
    ];
    return gradients[index % gradients.length];
  };

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`text-center mb-16 ${inView ? 'animate__animated animate__fadeInDown' : ''}`}>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                What Our <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Clients Say</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Discover why businesses across the country trust Nupura Self Drive Cars for their vehicle rental needs
              </p>
              
              {/* Overall Rating */}
              <div className="mt-8 flex justify-center items-center space-x-4">
                <div className="text-5xl font-bold text-gray-800">4.8</div>
                <div className="text-left">
                  <div className="flex space-x-1 mb-1">
                    {renderStars(5)}
                  </div>
                  <p className="text-gray-600 text-sm">Based on 500+ reviews</p>
                </div>
              </div>
            </div>
          )}
        </InView>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <InView key={index} triggerOnce={true} threshold={0.2}>
              {({ inView, ref }) => (
                <div
                  ref={ref}
                  className={`group relative bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transform transition-all duration-500 ${
                    inView 
                      ? 'animate__animated animate__fadeInUp opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-10'
                  } hover:-translate-y-3 border border-gray-100`}
                >
                  {/* Quote Icon */}
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    "
                  </div>

                  {/* Header with Avatar */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`w-16 h-16 bg-gradient-to-r ${getGradient(index)} rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                      {testimonial.avatar}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-600">{testimonial.position}</p>
                      <div className="flex space-x-1 mt-1">
                        {renderStars(testimonial.rating)}
                      </div>
                    </div>
                  </div>

                  {/* Feedback */}
                  <div className="relative">
                    <p className="text-gray-700 leading-relaxed text-sm">
                      "{testimonial.feedback}"
                    </p>
                  </div>

                  {/* Hover Effect Border */}
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${getGradient(index)} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                  {/* Decorative Corner */}
                  <div className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-gradient-to-r ${getGradient(index)} rounded-bl-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                </div>
              )}
            </InView>
          ))}
        </div>

        {/* Trust Indicators */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mt-16 ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white shadow-2xl">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                  <div>
                    <div className="text-3xl font-bold text-yellow-400 mb-2">500+</div>
                    <div className="text-purple-100">Happy Clients</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-400 mb-2">4.8/5</div>
                    <div className="text-purple-100">Average Rating</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cyan-400 mb-2">50+</div>
                    <div className="text-purple-100">Cities Served</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-pink-400 mb-2">98%</div>
                    <div className="text-purple-100">Satisfaction Rate</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </InView>

        {/* CTA Section */}
        <InView triggerOnce={true} threshold={0.3}>
          {({ inView, ref }) => (
            <div ref={ref} className={`mt-12 text-center ${inView ? 'animate__animated animate__fadeInUp' : ''}`}>
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-12 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 text-lg">
                Join Our Happy Clients
              </button>
            </div>
          )}
        </InView>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default ClientTestimonials;